import { Loader } from '@googlemaps/js-api-loader';

export type GeocodedLocation = {
  latitude: number;
  longitude: number;
};

const ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/;
const CITY_NAME_REGEX = /^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/;
const STATE_NAME_REGEX = /^[A-Za-z]+(?:\s[A-Za-z]+)*$/;
const GENERAL_LOCATION_REGEX = /^(?=.*[A-Za-z])[A-Za-z0-9\s,'-]{2,}$/;

const ACCEPTED_GEOCODER_TYPES: string[] = [
  'street_address',
  'route',
  'premise',
  'locality',
  'postal_code',
  'administrative_area_level_3',
];

const ensureGoogleMapsLoaded = async (mapApiKey?: string): Promise<void> => {
  if (typeof window === 'undefined') {
    return;
  }

  // Reuse Maps JS when already loaded (e.g. by useLoadScript) to avoid double-include errors.
  if (window.google?.maps) {
    return;
  }

  if (!mapApiKey) {
    return;
  }

  const loader = new Loader({
    apiKey: mapApiKey,
    version: 'weekly',
    libraries: ['geocoding'],
  });
  await loader.importLibrary('maps');
};

/**
 * Performs a lightweight, local format check on a raw location query.
 * Returns true for valid US ZIP codes, city/state names, or general address strings.
 */
export const isValidLocationQuery = (query: string): boolean => {
  const trimmed = query?.trim();

  if (!trimmed) {
    return false;
  }

  return (
    ZIP_CODE_REGEX.test(trimmed) ||
    CITY_NAME_REGEX.test(trimmed) ||
    STATE_NAME_REGEX.test(trimmed) ||
    GENERAL_LOCATION_REGEX.test(trimmed)
  );
};

const hasAcceptedGeocoderType = (types?: string[]): boolean => {
  if (!types || types.length === 0) {
    return false;
  }

  return types.some((type) => ACCEPTED_GEOCODER_TYPES.includes(type));
};

/**
 * Geocodes a US ZIP, city, and/or state string to coordinates.
 * Unlike getStateFromZipcodeCoveo, this accepts non–postal-code results (city/state).
 */
export const geocodeLocation = async (
  address: string,
  mapApiKey?: string
): Promise<GeocodedLocation | null> => {
  const trimmed = address?.trim();
  if (!trimmed || !isValidLocationQuery(trimmed)) {
    return null;
  }

  await ensureGoogleMapsLoaded(mapApiKey);

  if (typeof window === 'undefined' || !window.google?.maps?.Geocoder) {
    return null;
  }

  return new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { address: trimmed, componentRestrictions: { country: 'US' } },
      (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          const firstResult = results[0];

          if (firstResult.partial_match || !hasAcceptedGeocoderType(firstResult.types)) {
            resolve(null);
            return;
          }

          const location = firstResult?.geometry?.location;
          const latitude = location?.lat();
          const longitude = location?.lng();

          if (typeof latitude === 'number' && typeof longitude === 'number') {
            resolve({ latitude, longitude });
            return;
          }
        }
        resolve(null);
      }
    );
  });
};
